import React, {useEffect, useRef, useState} from 'react';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  adminTokenAbuseCache,
  adminTokenChangesCache,
  adminTokenRulesCache,
  adminUsersCache,
  adminUserTokensCache,
} from '../../../common/constants/queryCacheKey';
import {fetchAdminUsers} from '../../../common/services/users';
import {useGuardedMutate} from '../../../common/hooks/useGuardedMutate';
import {
  AbuseFlag,
  adjustUserTokens,
  fetchAbuseFlags,
  fetchTokenChanges,
  resolveAbuseFlag,
  fetchTokenRules,
  fetchUserTokens,
  setUserTrust,
  TokenConfigChange,
  TokenRule,
  TokenRuleKind,
  TokenSettings,
  TokenSettingsValues,
  TrustLevel,
  updateTokenRule,
  updateTokenSettings,
  UpdateTokenRuleInput,
  UpdateTokenSettingsInput,
  UserTokens,
} from '../../../common/services/tokens';
import {
  badgeStyle,
  chipStyle,
  disabledButtonStyle,
  errorStyle,
  inputStyle,
  labelStyle,
  noticeStyle,
  panelStyle,
  primaryButtonStyle,
  screenStyle,
  secondaryButtonStyle,
  titleStyle,
} from '../../admin/adminStyles';

// ---------- Rule metadata ----------

interface RuleMeta {
  label: string;
  hint?: string;
  // The rule's own Points field is ignored by the backend.
  pointsFixed?: boolean;
}

const RULE_ORDER: TokenRuleKind[] = [
  'BookingHonored',
  'BookingVerifiedVisit',
  'GoingVerifiedVisit',
  'NewPlaceVisit',
  'Review',
  'Mood',
  'ReactionReceived',
  'TripCompleted',
  'FriendAdded',
  'ChallengeCompleted',
  'AdminAdjustment',
];

const RULE_META: Record<TokenRuleKind, RuleMeta> = {
  BookingHonored: {label: 'Booking honored'},
  BookingVerifiedVisit: {label: 'Verified visit at a booking'},
  GoingVerifiedVisit: {label: 'Verified visit (Going)'},
  NewPlaceVisit: {label: 'New place visited'},
  Review: {label: 'Review written'},
  Mood: {label: 'Mood shared'},
  ReactionReceived: {label: 'Reaction received'},
  TripCompleted: {label: 'Trip completed'},
  FriendAdded: {label: 'Friend added'},
  ChallengeCompleted: {
    label: 'Challenge completed',
    hint: 'Points come from each challenge; only Enabled matters here.',
    pointsFixed: true,
  },
  AdminAdjustment: {
    label: 'Admin adjustment',
    hint: 'Has no points of its own; only Enabled matters.',
    pointsFixed: true,
  },
};

const ruleLabel = (kind: string): string =>
  (RULE_META as Record<string, RuleMeta | undefined>)[kind]?.label ?? kind;

// A change log target: "rule:{Kind}", "settings" or "trust:{userId}".
const targetLabel = (target: string): string => {
  if (target.startsWith('rule:')) return ruleLabel(target.slice(5));
  if (target === 'settings') return 'Settings';
  if (target.startsWith('trust:')) return `Trust · ${target.slice(6, 14)}`;
  return target;
};

const TRUST_LEVELS: TrustLevel[] = ['Normal', 'Limited', 'Blocked'];

const trustColors: Record<TrustLevel, string> = {
  Normal: '#2f6b2f',
  Limited: '#7a5a16',
  Blocked: '#7a2626',
};

// ---------- Helpers ----------

const formatDateTime = (value?: string | null): string =>
  value
    ? new Date(value).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

const signed = (points: number) => (points > 0 ? `+${points}` : `${points}`);

// The server's limits (UpdateTokenRuleHandler, AdjustUserTokensHandler).
const MAX_RULE_POINTS = 10000;
const MAX_ADJUSTMENT = 10000;
// The server appends an admin suffix, so the note itself must stay shorter.
const ADJUST_NOTE_MAX = 450;

// Empty means "no cap"; anything else must be a whole non-negative number.
const parseCap = (text: string): number | null | undefined => {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  const value = Number(trimmed);
  return Number.isInteger(value) && value >= 0 ? value : undefined;
};

const sortRules = (rules: TokenRule[]): TokenRule[] => {
  const position = (kind: string) => {
    const index = RULE_ORDER.indexOf(kind as TokenRuleKind);
    return index === -1 ? RULE_ORDER.length : index;
  };
  return [...rules].sort((a, b) => position(a.kind) - position(b.kind));
};

const compactValue = (value: unknown): string =>
  value === null || value === undefined ? 'none' : JSON.stringify(value);

// The keys whose values differ between the two JSON snapshots, as "key: before → after".
const diffLines = (change: TokenConfigChange): string[] => {
  const parse = (json: string | null): Record<string, unknown> | null => {
    if (json === null) return null;
    try {
      const parsed = JSON.parse(json);
      return parsed && typeof parsed === 'object' ? parsed : {value: parsed};
    } catch {
      return {value: json};
    }
  };

  const before = parse(change.beforeJson);
  const after = parse(change.afterJson) ?? {};
  if (before === null) {
    return ['created'];
  }

  const keys = Array.from(
    new Set([...Object.keys(before), ...Object.keys(after)]),
  ).filter(key => key !== 'updatedAt');

  // A key missing on one side and null on the other did not change.
  const same = (a: unknown, b: unknown) =>
    JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

  const lines = keys
    .filter(key => !same(before[key], after[key]))
    .map(
      key =>
        `${key}: ${compactValue(before[key])} → ${compactValue(after[key])}`,
    );

  return lines.length > 0 ? lines : ['no change'];
};

// ---------- Rules panel ----------

interface RuleForm {
  points: string;
  dailyCap: string;
  weeklyCap: string;
  enabled: boolean;
}

const toRuleForm = (rule: TokenRule): RuleForm => ({
  points: String(rule.points),
  dailyCap: rule.dailyCap === null ? '' : String(rule.dailyCap),
  weeklyCap: rule.weeklyCap === null ? '' : String(rule.weeklyCap),
  enabled: rule.enabled,
});

const isRuleChanged = (rule: TokenRule, form: RuleForm) => {
  const original = toRuleForm(rule);
  return (
    form.points.trim() !== original.points ||
    form.dailyCap.trim() !== original.dailyCap ||
    form.weeklyCap.trim() !== original.weeklyCap ||
    form.enabled !== original.enabled
  );
};

const RuleRow: React.FC<{
  rule: TokenRule;
  onSaved: (message: string) => void;
  onError: (message: string) => void;
}> = ({rule, onSaved, onError}) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<RuleForm>(() => toRuleForm(rule));
  const meta = RULE_META[rule.kind] ?? {label: rule.kind};
  const changed = isRuleChanged(rule, form);

  const saveMutation = useMutation({
    mutationFn: (input: UpdateTokenRuleInput) =>
      updateTokenRule(rule.kind, input),
    onSuccess: saved => {
      queryClient.invalidateQueries({queryKey: [adminTokenRulesCache]});
      queryClient.invalidateQueries({queryKey: [adminTokenChangesCache]});
      setForm(toRuleForm(saved));
      onSaved(`${meta.label} was saved.`);
    },
    onError: (e: Error) => onError(e.message),
  });

  const handleSave = () => {
    const pointsText = form.points.trim();
    const points = meta.pointsFixed ? rule.points : Number(pointsText);
    const dailyCap = parseCap(form.dailyCap);
    const weeklyCap = parseCap(form.weeklyCap);

    if (!meta.pointsFixed && pointsText === '') {
      onError(`${meta.label}: points is required.`);
      return;
    }
    if (!Number.isInteger(points) || points < 0 || points > MAX_RULE_POINTS) {
      onError(
        `${meta.label}: points must be a whole number between 0 and ${MAX_RULE_POINTS}.`,
      );
      return;
    }
    if (dailyCap === undefined || weeklyCap === undefined) {
      onError(
        `${meta.label}: caps must be whole numbers of 0 or more, or empty for no cap.`,
      );
      return;
    }
    if (dailyCap !== null && weeklyCap !== null && weeklyCap < dailyCap) {
      onError(`${meta.label}: the weekly cap must be at least the daily cap.`);
      return;
    }

    saveMutation.mutate({points, dailyCap, weeklyCap, enabled: form.enabled});
  };

  const canSave = changed && !saveMutation.isPending;

  return (
    <tr style={rowStyle}>
      <td style={cellStyle}>
        <div style={{color: '#fff'}}>{meta.label}</div>
        {meta.hint && <div style={hintStyle}>{meta.hint}</div>}
      </td>
      <td style={cellStyle}>
        <input
          type="number"
          min={0}
          step={1}
          max={MAX_RULE_POINTS}
          value={form.points}
          disabled={!!meta.pointsFixed}
          onChange={e => setForm({...form, points: e.target.value})}
          style={meta.pointsFixed ? disabledInputStyle : numberInputStyle}
        />
      </td>
      <td style={cellStyle}>
        <input
          type="number"
          min={0}
          step={1}
          placeholder="no cap"
          value={form.dailyCap}
          onChange={e => setForm({...form, dailyCap: e.target.value})}
          style={numberInputStyle}
        />
      </td>
      <td style={cellStyle}>
        <input
          type="number"
          min={0}
          step={1}
          placeholder="no cap"
          value={form.weeklyCap}
          onChange={e => setForm({...form, weeklyCap: e.target.value})}
          style={numberInputStyle}
        />
      </td>
      <td style={{...cellStyle, textAlign: 'center'}}>
        <input
          type="checkbox"
          checked={form.enabled}
          onChange={e => setForm({...form, enabled: e.target.checked})}
        />
      </td>
      <td style={{...cellStyle, textAlign: 'right'}}>
        <button
          onClick={handleSave}
          disabled={!canSave}
          style={canSave ? primaryButtonStyle : disabledButtonStyle}>
          {saveMutation.isPending ? 'Saving...' : 'Save'}
        </button>
      </td>
    </tr>
  );
};

// One entry per settings field: its label, hint and the range the server accepts.
interface SettingSpec {
  key: keyof TokenSettingsValues;
  label: string;
  hint?: string;
  min: number;
  max: number;
}

const CAP_FIELDS: SettingSpec[] = [
  {
    key: 'dailyCap',
    label: 'Daily cap',
    hint: 'Most points a user can earn across every rule per day.',
    min: 1,
    max: 100000,
  },
  {
    key: 'weeklyCap',
    label: 'Weekly cap',
    hint: 'Per week; must be at least the daily cap.',
    min: 1,
    max: 100000,
  },
];

const VERIFICATION_FIELDS: SettingSpec[] = [
  {
    key: 'maxVerifiedVisitsPerDay',
    label: 'Max verified visits per day',
    hint: 'QR and location together.',
    min: 0,
    max: 50,
  },
  {
    key: 'qrWindowBeforeMinutes',
    label: 'QR valid before start (min)',
    hint: 'How early a QR scan counts.',
    min: 0,
    max: 1440,
  },
  {
    key: 'qrWindowAfterMinutes',
    label: 'QR valid after end (min)',
    hint: 'After the start when the booking has no end.',
    min: 0,
    max: 1440,
  },
  {
    key: 'locationWindowBeforeMinutes',
    label: 'Location window before start (min)',
    hint: 'How early pings start to count.',
    min: 0,
    max: 1440,
  },
  {
    key: 'locationWindowAfterMinutes',
    label: 'Location window after start (min)',
    hint: 'When there is no end.',
    min: 0,
    max: 1440,
  },
  {
    key: 'locationMinPings',
    label: 'Min location pings',
    hint: "Inside the activity's cell.",
    min: 1,
    max: 100,
  },
  {
    key: 'locationMinSpanMinutes',
    label: 'Min span between first and last ping (min)',
    hint: 'Shorter stays are not a visit.',
    min: 0,
    max: 1440,
  },
  {
    key: 'maxSpeedKmh',
    label: 'Max speed between pings (km/h)',
    hint: 'Faster flags the user and voids the day.',
    min: 1,
    max: 2000,
  },
];

const SETTING_FIELDS = [...CAP_FIELDS, ...VERIFICATION_FIELDS];

type SettingsForm = Record<keyof TokenSettingsValues, string>;

const toSettingsForm = (settings: TokenSettingsValues): SettingsForm =>
  SETTING_FIELDS.reduce((form, field) => {
    form[field.key] = String(settings[field.key]);
    return form;
  }, {} as SettingsForm);

const SettingField: React.FC<{
  field: SettingSpec;
  value: string;
  onChange: (value: string) => void;
}> = ({field, value, onChange}) => (
  <label style={{...labelStyle, width: '220px'}}>
    {field.label}
    <input
      required
      type="number"
      min={field.min}
      max={field.max}
      step={1}
      value={value}
      onChange={e => onChange(e.target.value)}
      style={inputStyle}
    />
    {field.hint && <span style={hintStyle}>{field.hint}</span>}
  </label>
);

const SettingsPanel: React.FC<{
  settings: TokenSettings;
  onSaved: (message: string) => void;
  onError: (message: string) => void;
}> = ({settings, onSaved, onError}) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<SettingsForm>(() =>
    toSettingsForm(settings),
  );

  const set = (key: keyof TokenSettingsValues) => (value: string) =>
    setForm(current => ({...current, [key]: value}));

  // Only the fields that differ from the server copy are sent.
  const changes = (): UpdateTokenSettingsInput | string => {
    const input: UpdateTokenSettingsInput = {};
    for (const field of SETTING_FIELDS) {
      const text = form[field.key].trim();
      if (text === String(settings[field.key])) continue;
      const value = Number(text);
      if (
        text === '' ||
        !Number.isInteger(value) ||
        value < field.min ||
        value > field.max
      ) {
        return `${field.label} must be a whole number between ${field.min} and ${field.max}.`;
      }
      input[field.key] = value;
    }

    const daily = input.dailyCap ?? settings.dailyCap;
    const weekly = input.weeklyCap ?? settings.weeklyCap;
    if (weekly < daily) {
      return 'The weekly cap must be at least the daily cap.';
    }
    return input;
  };

  const changed = SETTING_FIELDS.some(
    field => form[field.key].trim() !== String(settings[field.key]),
  );

  const saveMutation = useMutation({
    mutationFn: (input: UpdateTokenSettingsInput) => updateTokenSettings(input),
    onSuccess: saved => {
      queryClient.invalidateQueries({queryKey: [adminTokenRulesCache]});
      queryClient.invalidateQueries({queryKey: [adminTokenChangesCache]});
      setForm(toSettingsForm(saved));
      onSaved('The settings were saved.');
    },
    onError: (e: Error) => onError(e.message),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const input = changes();
    if (typeof input === 'string') {
      onError(input);
      return;
    }
    if (Object.keys(input).length === 0) return;
    saveMutation.mutate(input);
  };

  const canSave = changed && !saveMutation.isPending;

  return (
    <form onSubmit={handleSubmit} style={settingsFormStyle}>
      <div>
        <h3 style={subTitleStyle}>Settings</h3>
        <p style={hintStyle}>
          Last changed {formatDateTime(settings.updatedAt)}.
        </p>
      </div>

      <div>
        <h4 style={groupTitleStyle}>Caps</h4>
        <div style={fieldGridStyle}>
          {CAP_FIELDS.map(field => (
            <SettingField
              key={field.key}
              field={field}
              value={form[field.key]}
              onChange={set(field.key)}
            />
          ))}
        </div>
      </div>

      <div>
        <h4 style={groupTitleStyle}>Verified visits</h4>
        <div style={fieldGridStyle}>
          {VERIFICATION_FIELDS.map(field => (
            <SettingField
              key={field.key}
              field={field}
              value={form[field.key]}
              onChange={set(field.key)}
            />
          ))}
        </div>
      </div>

      <div>
        <button
          type="submit"
          disabled={!canSave}
          style={canSave ? primaryButtonStyle : disabledButtonStyle}>
          {saveMutation.isPending ? 'Saving...' : 'Save'}
        </button>
      </div>
    </form>
  );
};

const RulesPanel: React.FC = () => {
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const rulesQuery = useQuery({
    queryKey: [adminTokenRulesCache],
    queryFn: fetchTokenRules,
  });

  const onSaved = (message: string) => {
    setError(null);
    setNotice(message);
  };
  const onError = (message: string) => {
    setNotice(null);
    setError(message);
  };

  const data = rulesQuery.data;

  return (
    <section style={sectionStyle}>
      <h2 style={sectionTitleStyle}>Rules</h2>

      {rulesQuery.isPending && <p style={loadingStyle}>Loading...</p>}
      {rulesQuery.isError && (
        <p style={errorStyle}>{(rulesQuery.error as Error).message}</p>
      )}

      {data && (
        <>
          <div style={{overflowX: 'auto'}}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={headStyle}>Rule</th>
                  <th style={headStyle}>Points</th>
                  <th style={headStyle}>Daily cap</th>
                  <th style={headStyle}>Weekly cap</th>
                  <th style={{...headStyle, textAlign: 'center'}}>Enabled</th>
                  <th style={headStyle} />
                </tr>
              </thead>
              <tbody>
                {sortRules(data.rules).map(rule => (
                  <RuleRow
                    // Remount when the server copy changes so the form follows it.
                    key={`${rule.kind}-${rule.updatedAt}`}
                    rule={rule}
                    onSaved={onSaved}
                    onError={onError}
                  />
                ))}
              </tbody>
            </table>
          </div>

          <SettingsPanel
            key={data.settings.updatedAt}
            settings={data.settings}
            onSaved={onSaved}
            onError={onError}
          />
        </>
      )}

      {notice && <p style={{...noticeStyle, marginTop: '16px'}}>{notice}</p>}
      {error && <p style={{...errorStyle, marginTop: '16px'}}>{error}</p>}
    </section>
  );
};

// ---------- User lookup panel ----------

const GUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const UserDetails: React.FC<{
  userId: string;
  pageNumber: number;
  onPageChange: (page: number) => void;
}> = ({userId, pageNumber, onPageChange}) => {
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [trustLevel, setTrustLevel] = useState<TrustLevel | ''>('');
  const [trustNote, setTrustNote] = useState('');
  const [adjustPoints, setAdjustPoints] = useState('');
  const [adjustNote, setAdjustNote] = useState('');

  const userQuery = useQuery({
    queryKey: [adminUserTokensCache, userId, pageNumber],
    queryFn: () => fetchUserTokens(userId, pageNumber),
    placeholderData: keepPreviousData,
    retry: false,
  });

  const refresh = () =>
    queryClient.invalidateQueries({queryKey: [adminUserTokensCache, userId]});

  const trustMutation = useMutation({
    mutationFn: (input: {trustLevel: TrustLevel; note?: string}) =>
      setUserTrust(userId, input),
    onSuccess: (_, input) => {
      setError(null);
      setNotice(`Trust level set to ${input.trustLevel}.`);
      setTrustNote('');
      refresh();
      // The flags show the trust level, and the change is logged.
      queryClient.invalidateQueries({queryKey: [adminTokenAbuseCache]});
      queryClient.invalidateQueries({queryKey: [adminTokenChangesCache]});
    },
    onError: (e: Error) => {
      setNotice(null);
      setError(e.message);
    },
  });

  const adjustMutation = useMutation({
    mutationFn: (input: {points: number; note: string}) =>
      adjustUserTokens(userId, input),
    onSuccess: entry => {
      setError(null);
      setNotice(`Balance adjusted by ${signed(entry.points)}.`);
      setAdjustPoints('');
      setAdjustNote('');
      refresh();
    },
    onError: (e: Error) => {
      setNotice(null);
      setError(e.message);
    },
  });

  const adjust = useGuardedMutate(adjustMutation.mutate);

  const user: UserTokens | undefined = userQuery.data;
  const selectedTrust = trustLevel || user?.trustLevel || 'Normal';

  const handleTrust = (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    if (
      selectedTrust === 'Blocked' &&
      !window.confirm(
        `Block ${user.displayName} from earning tokens?\n\nThey stop earning points until the trust level is changed back.`,
      )
    ) {
      return;
    }
    const note = trustNote.trim();
    trustMutation.mutate({
      trustLevel: selectedTrust,
      note: note || undefined,
    });
  };

  const handleAdjust = (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    const points = Number(adjustPoints);
    const note = adjustNote.trim();
    if (
      adjustPoints.trim() === '' ||
      !Number.isInteger(points) ||
      points === 0 ||
      Math.abs(points) > MAX_ADJUSTMENT
    ) {
      setNotice(null);
      setError(
        `Points must be a whole number other than 0, at most ${MAX_ADJUSTMENT} either way.`,
      );
      return;
    }
    if (!note) {
      setNotice(null);
      setError('A note is required for an adjustment.');
      return;
    }
    if (note.length > ADJUST_NOTE_MAX) {
      setNotice(null);
      setError(`The note can be at most ${ADJUST_NOTE_MAX} characters.`);
      return;
    }
    if (points < 0 && user.balance + points < 0) {
      setNotice(null);
      setError(
        `Cannot take ${Math.abs(points)} points: the balance is ${user.balance} and cannot go below zero.`,
      );
      return;
    }
    if (
      points < 0 &&
      !window.confirm(
        `Take ${Math.abs(points)} points from ${user.displayName}?\n\nTheir balance is ${user.balance}.`,
      )
    ) {
      return;
    }
    adjust({points, note});
  };

  if (userQuery.isPending) {
    return <p style={loadingStyle}>Loading...</p>;
  }

  if (userQuery.isError || !user) {
    return (
      <p style={errorStyle}>
        {userQuery.error instanceof Error
          ? userQuery.error.message
          : 'Could not load the user.'}
      </p>
    );
  }

  const ledger = user.ledger;
  const totalPages = ledger.totalPages;

  return (
    <div>
      <div style={userHeaderStyle}>
        <div style={{flex: 1, minWidth: '200px'}}>
          <div style={{color: '#fff', fontWeight: 'bold', fontSize: '1.05rem'}}>
            {user.displayName}
          </div>
          <div style={{color: '#999', fontSize: '0.85rem'}}>
            {user.email ?? 'No email'}
          </div>
          <div style={{color: '#666', fontSize: '0.75rem', marginTop: '2px'}}>
            {user.userId}
          </div>
        </div>
        <span
          style={{
            ...badgeStyle,
            backgroundColor: trustColors[user.trustLevel],
          }}>
          {user.trustLevel}
        </span>
      </div>

      <div style={statsStyle}>
        <Stat label="Balance" value={user.balance} />
        <Stat label="Lifetime" value={user.lifetimePoints} />
        <Stat label="Earned today" value={user.earnedToday} />
        <Stat label="Earned this week" value={user.earnedThisWeek} />
      </div>

      <div style={{display: 'flex', gap: '16px', flexWrap: 'wrap'}}>
        <form onSubmit={handleTrust} style={subFormStyle}>
          <h3 style={subTitleStyle}>Trust level</h3>
          <div style={subFormRowStyle}>
            <label style={{...labelStyle, width: '140px'}}>
              Level
              <select
                value={selectedTrust}
                onChange={e => setTrustLevel(e.target.value as TrustLevel)}
                style={inputStyle}>
                {TRUST_LEVELS.map(level => (
                  <option key={level} value={level}>
                    {level}
                  </option>
                ))}
              </select>
            </label>
            <label style={{...labelStyle, flex: 1, minWidth: '160px'}}>
              Note (optional)
              <input
                value={trustNote}
                onChange={e => setTrustNote(e.target.value)}
                style={inputStyle}
              />
            </label>
            <button
              type="submit"
              disabled={trustMutation.isPending}
              style={
                trustMutation.isPending
                  ? disabledButtonStyle
                  : selectedTrust === 'Blocked'
                    ? dangerSolidButtonStyle
                    : primaryButtonStyle
              }>
              {trustMutation.isPending ? 'Applying...' : 'Apply'}
            </button>
          </div>
        </form>

        <form onSubmit={handleAdjust} style={subFormStyle}>
          <h3 style={subTitleStyle}>Adjust</h3>
          <div style={subFormRowStyle}>
            <label style={{...labelStyle, width: '140px'}}>
              Points (± allowed)
              <input
                required
                type="number"
                step={1}
                min={-MAX_ADJUSTMENT}
                max={MAX_ADJUSTMENT}
                value={adjustPoints}
                onChange={e => setAdjustPoints(e.target.value)}
                style={inputStyle}
              />
            </label>
            <label style={{...labelStyle, flex: 1, minWidth: '160px'}}>
              Note (required)
              <input
                required
                maxLength={ADJUST_NOTE_MAX}
                value={adjustNote}
                onChange={e => setAdjustNote(e.target.value)}
                style={inputStyle}
              />
            </label>
            <button
              type="submit"
              disabled={adjustMutation.isPending}
              style={
                adjustMutation.isPending
                  ? disabledButtonStyle
                  : primaryButtonStyle
              }>
              {adjustMutation.isPending ? 'Adjusting...' : 'Adjust'}
            </button>
          </div>
        </form>
      </div>

      {notice && <p style={{...noticeStyle, marginTop: '16px'}}>{notice}</p>}
      {error && <p style={{...errorStyle, marginTop: '16px'}}>{error}</p>}

      <h3 style={{...subTitleStyle, marginTop: '20px'}}>
        Ledger{' '}
        <span style={{color: '#666', fontWeight: 'normal'}}>
          ({ledger.totalCount})
        </span>
      </h3>
      {ledger.items.length === 0 ? (
        <p style={hintStyle}>No entries yet.</p>
      ) : (
        <div style={{overflowX: 'auto'}}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={headStyle}>Date</th>
                <th style={headStyle}>Rule</th>
                <th style={{...headStyle, textAlign: 'right'}}>Points</th>
                <th style={headStyle}>Source</th>
                <th style={headStyle}>Note</th>
                <th style={headStyle} />
              </tr>
            </thead>
            <tbody>
              {ledger.items.map(entry => (
                <tr key={entry.id} style={rowStyle}>
                  <td style={{...cellStyle, whiteSpace: 'nowrap'}}>
                    {formatDateTime(entry.createdAt)}
                  </td>
                  <td style={cellStyle}>{ruleLabel(entry.rule)}</td>
                  <td
                    style={{
                      ...cellStyle,
                      textAlign: 'right',
                      fontWeight: 'bold',
                      color: entry.points >= 0 ? '#5ad35a' : '#ff4d4d',
                    }}>
                    {signed(entry.points)}
                  </td>
                  <td style={cellStyle} title={entry.sourceId ?? ''}>
                    {entry.sourceKind}
                  </td>
                  <td style={{...cellStyle, color: '#bbb'}}>
                    {entry.note ?? ''}
                  </td>
                  <td style={cellStyle}>
                    {entry.isReversal && (
                      <span style={badgeStyle}>reversal</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <Pager
          page={pageNumber}
          maxPage={totalPages}
          onChange={onPageChange}
          isFetching={userQuery.isFetching}
        />
      )}
    </div>
  );
};

const Stat: React.FC<{label: string; value: number}> = ({label, value}) => (
  <div style={statStyle}>
    <div style={{color: '#999', fontSize: '0.75rem'}}>{label}</div>
    <div style={{color: '#fff', fontWeight: 'bold', fontSize: '1.2rem'}}>
      {value}
    </div>
  </div>
);

// A request to show a user, e.g. from an abuse flag. The counter makes the
// same id loadable twice in a row.
interface LookupRequest {
  userId: string;
  sequence: number;
}

const UserLookupPanel: React.FC<{request: LookupRequest | null}> = ({
  request,
}) => {
  const [input, setInput] = useState('');
  const [userId, setUserId] = useState<string | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [inputError, setInputError] = useState<string | null>(null);
  // A name or email to look up; a GUID loads the user directly instead.
  const [searchKeyword, setSearchKeyword] = useState<string | null>(null);
  const sectionRef = useRef<HTMLElement>(null);

  const searchQuery = useQuery({
    queryKey: [adminUsersCache, searchKeyword, 1],
    queryFn: () => fetchAdminUsers(searchKeyword ?? '', 1, 10),
    enabled: searchKeyword !== null,
  });

  const loadUser = (id: string) => {
    setInputError(null);
    setSearchKeyword(null);
    setPageNumber(1);
    setUserId(id.toLowerCase());
  };

  useEffect(() => {
    if (!request) return;
    setInput(request.userId);
    loadUser(request.userId);
    sectionRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'});
  }, [request]);

  const handleLoad = (e: React.FormEvent) => {
    e.preventDefault();
    const value = input.trim();
    if (GUID_PATTERN.test(value)) {
      loadUser(value);
      return;
    }
    if (value.length < 2) {
      setInputError('Enter a name, an email or a user id.');
      return;
    }
    setInputError(null);
    setSearchKeyword(value);
  };

  const matches = searchQuery.data?.items ?? [];

  return (
    <section ref={sectionRef} style={sectionStyle}>
      <h2 style={sectionTitleStyle}>User lookup</h2>

      <form onSubmit={handleLoad} style={lookupFormStyle}>
        <label style={{...labelStyle, flex: 1, minWidth: '240px'}}>
          Name, email or user id
          <input
            placeholder="e.g. ana@example.com"
            value={input}
            onChange={e => setInput(e.target.value)}
            style={inputStyle}
          />
        </label>
        <button type="submit" style={primaryButtonStyle}>
          {searchQuery.isFetching ? 'Searching...' : 'Find'}
        </button>
      </form>
      {inputError && <p style={errorStyle}>{inputError}</p>}

      {searchKeyword !== null && (
        <div style={{marginBottom: '16px'}}>
          {searchQuery.isError && (
            <p style={errorStyle}>{(searchQuery.error as Error).message}</p>
          )}
          {searchQuery.isSuccess && matches.length === 0 && (
            <p style={hintStyle}>No user matches "{searchKeyword}".</p>
          )}
          {matches.map(match => (
            <div key={match.id} style={matchRowStyle}>
              <div style={{flex: '1 1 200px', minWidth: 0}}>
                <div style={{color: '#fff'}}>{match.fullName}</div>
                <div style={{color: '#999', fontSize: '0.8rem'}}>
                  {match.email}
                </div>
              </div>
              <button
                type="button"
                onClick={() => loadUser(match.id)}
                style={secondaryButtonStyle}>
                Load
              </button>
            </div>
          ))}
          {(searchQuery.data?.totalItems ?? 0) > matches.length && (
            <p style={hintStyle}>
              Showing the first {matches.length} of{' '}
              {searchQuery.data?.totalItems}; type more to narrow it down.
            </p>
          )}
        </div>
      )}

      {userId && (
        <UserDetails
          key={userId}
          userId={userId}
          pageNumber={pageNumber}
          onPageChange={setPageNumber}
        />
      )}
    </section>
  );
};

// ---------- Abuse flags panel ----------

const ABUSE_REASON_LABELS: Record<string, string> = {
  impossible_speed: 'Impossible speed between pings',
  mock_location: 'Mocked location reported',
  too_many_visits: 'Too many verified visits in a day',
  qr_outside_window: 'QR scanned outside its window',
};

const abuseReasonLabel = (reason: string) =>
  ABUSE_REASON_LABELS[reason] ?? reason;

const AbuseFlagsPanel: React.FC<{onLoadUser: (userId: string) => void}> = ({
  onLoadUser,
}) => {
  const queryClient = useQueryClient();
  const [openOnly, setOpenOnly] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const flagsQuery = useQuery({
    queryKey: [adminTokenAbuseCache, openOnly, pageNumber],
    queryFn: () => fetchAbuseFlags({open: openOnly, page: pageNumber}),
    placeholderData: keepPreviousData,
  });

  const resolveMutation = useMutation({
    mutationFn: (flag: AbuseFlag) => resolveAbuseFlag(flag.id),
    onSuccess: (_, flag) => {
      setError(null);
      setNotice(`The flag on ${flag.displayName} was resolved.`);
      queryClient.invalidateQueries({queryKey: [adminTokenAbuseCache]});
    },
    onError: (e: Error) => {
      setNotice(null);
      setError(e.message);
    },
  });

  const page = flagsQuery.data;
  const items = page?.items ?? [];

  return (
    <section style={sectionStyle}>
      <div style={abuseToolbarStyle}>
        <h2 style={{...sectionTitleStyle, margin: 0, flex: 1}}>Abuse flags</h2>
        <div style={{display: 'flex', gap: '6px'}}>
          {[
            {label: 'Open only', value: true},
            {label: 'All', value: false},
          ].map(option => (
            <button
              key={option.label}
              type="button"
              onClick={() => {
                setOpenOnly(option.value);
                setPageNumber(1);
              }}
              style={chipStyle(openOnly === option.value)}>
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {notice && <p style={noticeStyle}>{notice}</p>}
      {error && <p style={errorStyle}>{error}</p>}

      {flagsQuery.isPending && <p style={loadingStyle}>Loading...</p>}
      {flagsQuery.isError && (
        <p style={errorStyle}>{(flagsQuery.error as Error).message}</p>
      )}
      {page && items.length === 0 && (
        <p style={hintStyle}>{openOnly ? 'No open flags.' : 'No flags yet.'}</p>
      )}

      {items.length > 0 && (
        <div style={{overflowX: 'auto'}}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={headStyle}>Date</th>
                <th style={headStyle}>User</th>
                <th style={headStyle}>Reason</th>
                <th style={headStyle}>Details</th>
                <th style={headStyle}>Trust</th>
                <th style={{...headStyle, textAlign: 'right'}}>Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map(flag => (
                <tr key={flag.id} style={rowStyle}>
                  <td style={{...cellStyle, whiteSpace: 'nowrap'}}>
                    {formatDateTime(flag.createdAt)}
                  </td>
                  <td style={cellStyle}>
                    <div style={{color: '#fff'}}>{flag.displayName}</div>
                    <button
                      type="button"
                      onClick={() => onLoadUser(flag.userId)}
                      title={flag.userId}
                      style={linkButtonStyle}>
                      Load
                    </button>
                  </td>
                  <td style={cellStyle} title={flag.reason}>
                    {abuseReasonLabel(flag.reason)}
                  </td>
                  <td style={{...cellStyle, color: '#bbb'}}>
                    {flag.details ?? ''}
                  </td>
                  <td style={cellStyle}>
                    <span
                      style={{
                        ...badgeStyle,
                        backgroundColor: trustColors[flag.trustLevel],
                      }}>
                      {flag.trustLevel}
                    </span>
                  </td>
                  <td
                    style={{
                      ...cellStyle,
                      textAlign: 'right',
                      whiteSpace: 'nowrap',
                    }}>
                    {flag.resolvedAt ? (
                      <span style={{color: '#999'}}>
                        Resolved {formatDateTime(flag.resolvedAt)}
                      </span>
                    ) : (
                      <button
                        type="button"
                        disabled={resolveMutation.isPending}
                        onClick={() => {
                          setNotice(null);
                          resolveMutation.mutate(flag);
                        }}
                        style={
                          resolveMutation.isPending
                            ? disabledButtonStyle
                            : secondaryButtonStyle
                        }>
                        Resolve
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {page && page.maxPage > 1 && (
        <Pager
          page={pageNumber}
          maxPage={page.maxPage}
          onChange={setPageNumber}
          isFetching={flagsQuery.isFetching}
        />
      )}
    </section>
  );
};

// ---------- Change log panel ----------

const ChangeLogPanel: React.FC = () => {
  const [pageNumber, setPageNumber] = useState(1);

  const changesQuery = useQuery({
    queryKey: [adminTokenChangesCache, pageNumber],
    queryFn: () => fetchTokenChanges(pageNumber),
    placeholderData: keepPreviousData,
  });

  const page = changesQuery.data;

  return (
    <section style={sectionStyle}>
      <h2 style={sectionTitleStyle}>Change log</h2>

      {changesQuery.isPending && <p style={loadingStyle}>Loading...</p>}
      {changesQuery.isError && (
        <p style={errorStyle}>{(changesQuery.error as Error).message}</p>
      )}

      {page && page.items.length === 0 && (
        <p style={hintStyle}>No changes yet.</p>
      )}

      {page && page.items.length > 0 && (
        <div style={{overflowX: 'auto'}}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={headStyle}>Date</th>
                <th style={headStyle}>Admin</th>
                <th style={headStyle}>Target</th>
                <th style={headStyle}>Change</th>
              </tr>
            </thead>
            <tbody>
              {page.items.map(change => (
                <tr key={change.id} style={rowStyle}>
                  <td style={{...cellStyle, whiteSpace: 'nowrap'}}>
                    {formatDateTime(change.createdAt)}
                  </td>
                  <td style={cellStyle} title={change.adminUserId ?? undefined}>
                    {change.adminName ?? 'Unknown admin'}
                  </td>
                  <td style={cellStyle} title={change.target}>
                    {targetLabel(change.target)}
                  </td>
                  <td style={{...cellStyle, color: '#bbb'}}>
                    {diffLines(change).map(line => (
                      <div key={line} style={{fontFamily: 'monospace'}}>
                        {line}
                      </div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {page && page.maxPage > 1 && (
        <Pager
          page={pageNumber}
          maxPage={page.maxPage}
          onChange={setPageNumber}
          isFetching={changesQuery.isFetching}
        />
      )}
    </section>
  );
};

// ---------- Shared bits ----------

const Pager: React.FC<{
  page: number;
  maxPage: number;
  onChange: (page: number) => void;
  isFetching?: boolean;
}> = ({page, maxPage, onChange, isFetching}) => {
  const hasPrev = page > 1 && !isFetching;
  const hasNext = page < maxPage && !isFetching;
  return (
    <div style={paginationStyle}>
      <button
        onClick={() => onChange(page - 1)}
        disabled={!hasPrev}
        style={hasPrev ? secondaryButtonStyle : disabledButtonStyle}>
        Prev
      </button>
      <span style={{color: '#999'}}>
        Page {page} of {maxPage}
      </span>
      <button
        onClick={() => onChange(page + 1)}
        disabled={!hasNext}
        style={hasNext ? secondaryButtonStyle : disabledButtonStyle}>
        Next
      </button>
    </div>
  );
};

export const TokensScreen: React.FC = () => {
  const [lookup, setLookup] = useState<LookupRequest | null>(null);

  return (
    <div style={screenStyle}>
      <div style={{maxWidth: '1000px', margin: '0 auto'}}>
        <h1 style={titleStyle}>Tokens</h1>
        <RulesPanel />
        <UserLookupPanel request={lookup} />
        <AbuseFlagsPanel
          onLoadUser={userId =>
            setLookup(current => ({
              userId,
              sequence: (current?.sequence ?? 0) + 1,
            }))
          }
        />
        <ChangeLogPanel />
      </div>
    </div>
  );
};

// ---------- Styles ----------

const sectionStyle: React.CSSProperties = {
  ...panelStyle,
  marginBottom: '24px',
};

const sectionTitleStyle: React.CSSProperties = {
  color: '#fff',
  margin: '0 0 16px',
  fontSize: '1.2rem',
};

const subTitleStyle: React.CSSProperties = {
  color: '#fff',
  margin: '0 0 8px',
  fontSize: '1rem',
};

const loadingStyle: React.CSSProperties = {
  color: '#999',
  margin: 0,
};

const hintStyle: React.CSSProperties = {
  color: '#777',
  fontSize: '0.75rem',
  margin: '2px 0 0',
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.85rem',
};

const headStyle: React.CSSProperties = {
  textAlign: 'left',
  color: '#999',
  fontWeight: 'normal',
  fontSize: '0.75rem',
  padding: '6px 8px',
  borderBottom: '1px solid #333',
  whiteSpace: 'nowrap',
};

const rowStyle: React.CSSProperties = {
  borderBottom: '1px solid #2e2e2e',
};

const cellStyle: React.CSSProperties = {
  padding: '8px',
  color: '#ddd',
  verticalAlign: 'middle',
};

const numberInputStyle: React.CSSProperties = {
  ...inputStyle,
  height: '34px',
  width: '110px',
};

const disabledInputStyle: React.CSSProperties = {
  ...numberInputStyle,
  color: '#555',
  cursor: 'not-allowed',
};

const settingsFormStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  marginTop: '20px',
  paddingTop: '16px',
  borderTop: '1px solid #333',
};

const groupTitleStyle: React.CSSProperties = {
  color: '#999',
  fontSize: '0.8rem',
  fontWeight: 'bold',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  margin: '0 0 10px',
};

const fieldGridStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '12px',
};

const lookupFormStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-end',
  gap: '12px',
  marginBottom: '16px',
};

const matchRowStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: '12px',
  padding: '8px 0',
  borderBottom: '1px solid #2e2e2e',
};

const userHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  flexWrap: 'wrap',
  marginBottom: '12px',
};

const statsStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
  gap: '10px',
  marginBottom: '16px',
};

const statStyle: React.CSSProperties = {
  padding: '10px 12px',
  borderRadius: '8px',
  backgroundColor: '#2a2a2a',
  border: '1px solid #333',
};

const subFormStyle: React.CSSProperties = {
  flex: 1,
  minWidth: '300px',
  padding: '12px',
  borderRadius: '8px',
  border: '1px solid #333',
};

const subFormRowStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-end',
  gap: '10px',
};

const dangerSolidButtonStyle: React.CSSProperties = {
  ...primaryButtonStyle,
  backgroundColor: '#ff4d4d',
  border: '1px solid #ff4d4d',
  color: '#fff',
};

const abuseToolbarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '12px',
  marginBottom: '16px',
};

const linkButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  color: '#daff00',
  fontSize: '0.75rem',
  cursor: 'pointer',
  textDecoration: 'underline',
};

const paginationStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '16px',
  marginTop: '16px',
};
